'use client'

import { UseFormRegister, FieldErrors } from 'react-hook-form'
import { EMAIL_REGEX } from '@/validation'

type JobApplicationFormData = {
  name: string
  email: string
  location: string
  resume: FileList | null
  additionalNotes: string
}

const JobApplicationFields = ({
  register,
  errors,
  resumeFile,
}: JobApplicationFieldsProps) => {
  return (
    <>
      {/* Name */}
      <div className="mb-3">
        <label htmlFor="ContentPlaceHolder1_nameInput" className="block text-sm font-bold mb-1">
          Name
        </label>
        <input
          {...register('name', { required: 'Name is required' })}
          type="text"
          id="ContentPlaceHolder1_nameInput"
          name="name"
          placeholder="Enter your name"
          className={`w-full border rounded-lg p-2 ${
            errors.name ? 'border-red-500 focus:ring-red-300' : 'border-gray-300'
          } focus:outline-none focus:ring-2 focus:ring-blue-200`}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
        )}
      </div>

      {/* Email */}
      <div className="mb-3">
        <label htmlFor="ContentPlaceHolder1_emailAddressInput" className="block text-sm font-bold mb-1">
          Email address
        </label>
        <input
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: EMAIL_REGEX,
              message: 'Invalid email address',
            },
          })}
          id="ContentPlaceHolder1_emailAddressInput"
          type="email"
          name="email"
          placeholder="Enter your email"
          className={`w-full border rounded-lg p-2 form-control input-boxes ${
            errors.email ? 'border-red-500 focus:ring-red-300' : 'border-gray-300'
          } focus:outline-none focus:ring-2 focus:ring-blue-200`}
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Location */}
      <div className="mb-3">
        <label htmlFor="ContentPlaceHolder1_locationInput" className="block text-sm font-bold mb-1">
          Location
        </label>
        <input
          {...register('location')}
          id="ContentPlaceHolder1_locationInput"
          type="text"
          name="location"
          placeholder="Enter your location"
          className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <p className="text-xs text-gray-500 mt-1">
          Please take a moment to ensure you are applying for a remote job, or a job in your location
        </p>
      </div>

      {/* Resume Upload */}
      <div className="mb-3 input-boxes">
        <div className="border rounded p-3">
          <label htmlFor="ContentPlaceHolder1_resumeUpload" className="block text-sm font-bold mb-1">
            Upload your resume
          </label>
          <input
            {...register('resume', { required: 'Resume is required' })}
            id="ContentPlaceHolder1_resumeUpload"
            type="file"
            name="resume"
            accept=".pdf,.doc,.docx,.odt,.txt,.rtf,.pages"
            className={`block w-full text-sm text-gray-900
                     file:mr-3 file:py-1.5 file:px-4 
                     file:rounded-md file:border-0
                     file:text-sm file:font-semibold
                     file:bg-gray-100 file:text-black
                     hover:file:bg-gray-200 cursor-pointer ${
                       errors.resume ? 'border-red-500' : ''
                     }`}
          />
          {errors.resume && (
            <p className="text-red-500 text-sm mt-1">{errors.resume.message}</p>
          )}
          {resumeFile && (
            <p className="text-xs text-gray-600 mt-1">
              Selected: {resumeFile.name} ({(resumeFile.size / 1024).toFixed(2)} KB)
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            File size must be under 4MB. Accepted formats: PDF, DOC, DOCX, ODT, TXT, RTF, Pages
          </p>
        </div>
      </div>

      {/* Notes */}
      <div className="mb-3">
        <label htmlFor="ContentPlaceHolder1_additionalInformationInput" className="block text-sm font-bold mb-1">
          Additional notes
        </label>
        <textarea
          {...register('additionalNotes')}
          id="ContentPlaceHolder1_additionalInformationInput"
          name="additionalNotes"
          placeholder="Any additional information you'd like to share"
          rows={4}
          className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      </div>
    </>
  )
}

export default JobApplicationFields

interface JobApplicationFieldsProps {
  register: UseFormRegister<JobApplicationFormData>
  errors: FieldErrors<JobApplicationFormData>
  resumeFile: File | undefined
}
